import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { GLOBAL_SHORTCUTS } from '../../config/accessibilityConfig';

/**
 * Binds every chord in `GLOBAL_SHORTCUTS`.
 *
 * S132: the shortcuts catalogue advertised twelve chords and two of them reached
 * an action, because nothing in the app listened for the other ten. This is the
 * listener. It renders nothing; it exists so that the dialog describing these
 * keys is describing something real.
 *
 * Mounted inside the Router (it navigates) and above the routes, so the chords
 * work on every page. `useKeyboardShortcuts` already declines to fire while the
 * child is typing in an input, textarea or contenteditable, so Alt+N in the chat
 * box does not throw away what they were writing.
 */
export const GlobalShortcuts: React.FC = () => {
  const navigate = useNavigate();

  const handlers = useMemo(() => {
    const map: Record<string, () => void> = {};

    for (const [chord, def] of Object.entries(GLOBAL_SHORTCUTS)) {
      if (def.action.type === 'navigate') {
        const to = def.action.to;
        map[chord] = () => navigate(to);
      } else {
        const targetId = def.action.targetId;
        map[chord] = () => {
          const target = document.getElementById(targetId);
          if (!target) return;
          // <main> is not focusable by default, so "skip to main content" would
          // move the caret nowhere and the next Tab would resume from the
          // header — the exact failure the skip link exists to prevent.
          if (!target.hasAttribute('tabindex')) {
            target.setAttribute('tabindex', '-1');
          }
          target.focus();
          target.scrollIntoView({ block: 'start' });
        };
      }
    }

    return map;
  }, [navigate]);

  useKeyboardShortcuts(handlers);

  return null;
};
